import DecimalType from "decimal.js";

import { Allergen, PricingPlan, SubscriptionItemType } from "@/primary-models";

export enum FreshExtraProduct {
  SPRINKLES = "SPRINKLES",
  NIBBLES_CHICKEN = "NIBBLES_CHICKEN",
  NIBBLES_SALMON = "NIBBLES_SALMON",
  NIBBLES_CHICKEN_POUCH = "NIBBLES_CHICKEN_POUCH",
  NIBBLES_SALMON_POUCH = "NIBBLES_SALMON_POUCH",
  NIBBLES_TUNA_POUCH = "NIBBLES_TUNA_POUCH",
}

export type TFreshExtraProduct = keyof typeof FreshExtraProduct;

export type TNibblesProduct =
  | "NIBBLES_CHICKEN"
  | "NIBBLES_SALMON"
  | "NIBBLES_CHICKEN_POUCH"
  | "NIBBLES_SALMON_POUCH"
  | "NIBBLES_TUNA_POUCH";

export type TNibblesPouchProduct = Extract<TNibblesProduct,
  | "NIBBLES_CHICKEN_POUCH"
  | "NIBBLES_SALMON_POUCH"
  | "NIBBLES_TUNA_POUCH"
>;

export type TSprinklesProduct = Extract<TFreshExtraProduct, "SPRINKLES">;

export type FreshExtraItemQuantity = Partial<Record<TFreshExtraProduct, number>>;
export type FreshExtraItemPostChargeQuantity = Partial<Record<TFreshExtraProduct, number | null>>;

export interface IUpdateExtraItemRequest {
  subscriptionId: number;
  items: FreshExtraItemQuantity;
}

export interface IUpdateExtraItemResponse {
  subscriptionId: number;
  items: FreshExtraItemQuantity;
}

export interface INibblesUpsellRequest {
  product: TNibblesProduct;
}

export interface INibblesUpsellResponse {
  /** The subscription id to which the product was added */
  subscriptionId: number;
}

// Each extras item occupies a certain number of centiJars (base units)of space in the box
export const CENTIJAR_UNITS_PER_ITEM: Record<TFreshExtraProduct, number> = {
  [FreshExtraProduct.SPRINKLES]: 100,
  [FreshExtraProduct.NIBBLES_CHICKEN_POUCH]: 150,
  [FreshExtraProduct.NIBBLES_SALMON_POUCH]: 150,
  [FreshExtraProduct.NIBBLES_TUNA_POUCH]: 150,
  [FreshExtraProduct.NIBBLES_CHICKEN]: 300,
  [FreshExtraProduct.NIBBLES_SALMON]: 300,
} as const;

// Total capacity of standard box (fresh-box-2 or fresh-box-3) = 12 tubes or 3600 centiJars
// Total capacity of trial/freezer box (fresh-box-1) = 6 tubes or 1800 centiJars
type ALLOWED_CENTIJAR_UNITS_IN_BOX = 1800 | 3600;

export const MAX_FRESH_EXTRAS_CAPACITY_PER_BOX: Record<PricingPlan, ALLOWED_CENTIJAR_UNITS_IN_BOX> = {
  [PricingPlan.TRIAL]: 1800,
  [PricingPlan.TRIAL_WEEK]: 1800,
  [PricingPlan.FREEZER]: 1800,
  [PricingPlan.STANDARD]: 3600,
  [PricingPlan.HALF]: 3600,
  [PricingPlan.SAVER]: 3600,
} as const;

export const ALLERGEN_TO_EXTRAS_MAP: Record<Allergen, TFreshExtraProduct[]> = {
  [Allergen.CHICKEN]: [
    FreshExtraProduct.SPRINKLES,
    FreshExtraProduct.NIBBLES_CHICKEN,
    FreshExtraProduct.NIBBLES_CHICKEN_POUCH,
  ],
  [Allergen.TURKEY]: [],
  [Allergen.BEEF]: [],
  [Allergen.PORK]: [],
  [Allergen.LAMB]: [],
  [Allergen.WHITEFISH]: [],
  [Allergen.DUCK]: [],
  [Allergen.SALMON]: [
    FreshExtraProduct.NIBBLES_SALMON,
    FreshExtraProduct.NIBBLES_SALMON_POUCH,
  ],
  [Allergen.TUNA]: [
    FreshExtraProduct.NIBBLES_TUNA_POUCH,
  ],
};

// This is mostly to remind us to update the fresh extra product enum when we add a new recipe.
export const IS_FRESH_EXTRA_PRODUCT: Record<TFreshExtraProduct, true>
& Record<Exclude<SubscriptionItemType, TFreshExtraProduct>, false> = {
  [SubscriptionItemType.CLUCK]: false,
  [SubscriptionItemType.GOBBLE]: false,
  [SubscriptionItemType.MOOO]: false,
  [SubscriptionItemType.OINK]: false,
  [SubscriptionItemType.QUACK]: false,
  [SubscriptionItemType.SPLASH]: false,
  [SubscriptionItemType.BAAA]: false,
  [SubscriptionItemType.SWISH]: false,
  [SubscriptionItemType.SCOOP_PLANET]: false,
  [SubscriptionItemType.SCOOP_HEALTH]: false,
  [SubscriptionItemType.NIBBLES_SALMON]: true,
  [SubscriptionItemType.NIBBLES_CHICKEN]: true,
  [SubscriptionItemType.NIBBLES_SALMON_POUCH]: true,
  [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: true,
  [SubscriptionItemType.NIBBLES_TUNA_POUCH]: true,
  [SubscriptionItemType.SPRINKLES]: true,
  [SubscriptionItemType.TOTE_BAG]: false,
  [SubscriptionItemType.SHREDDED_CLUCK]: false,
  [SubscriptionItemType.SHREDDED_GOBBLE]: false,
};

export function isFreshExtraProduct(product: string): product is TFreshExtraProduct {
  return !!(IS_FRESH_EXTRA_PRODUCT as Partial<Record<string, boolean>>)[product];
}

export type TExtrasPriceInfo = Record<SubscriptionItemType, DecimalType>;

export const IS_NIBBLES_PRODUCT: Record<TNibblesProduct, true>
  & Record<Exclude<SubscriptionItemType, TNibblesProduct>, false> = {
    [SubscriptionItemType.CLUCK]: false,
    [SubscriptionItemType.GOBBLE]: false,
    [SubscriptionItemType.MOOO]: false,
    [SubscriptionItemType.OINK]: false,
    [SubscriptionItemType.QUACK]: false,
    [SubscriptionItemType.SPLASH]: false,
    [SubscriptionItemType.BAAA]: false,
    [SubscriptionItemType.SWISH]: false,
    [SubscriptionItemType.SCOOP_PLANET]: false,
    [SubscriptionItemType.SCOOP_HEALTH]: false,
    [SubscriptionItemType.NIBBLES_SALMON]: true,
    [SubscriptionItemType.NIBBLES_CHICKEN]: true,
    [SubscriptionItemType.NIBBLES_SALMON_POUCH]: true,
    [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: true,
    [SubscriptionItemType.NIBBLES_TUNA_POUCH]: true,
    [SubscriptionItemType.SPRINKLES]: false,
    [SubscriptionItemType.TOTE_BAG]: false,
    [SubscriptionItemType.SHREDDED_CLUCK]: false,
    [SubscriptionItemType.SHREDDED_GOBBLE]: false,
  };

export function isNibblesProduct(product: string): product is TNibblesProduct {
  return !!(IS_NIBBLES_PRODUCT as Partial<Record<string, boolean>>)[product];
}

export const NIBBLES_PRODUCTS = Object.values(SubscriptionItemType).filter(isNibblesProduct);

export const IS_NIBBLES_POUCH_PRODUCT: Record<TNibblesPouchProduct, true>
  & Record<Exclude<SubscriptionItemType, TNibblesPouchProduct>, false> = {
    [SubscriptionItemType.CLUCK]: false,
    [SubscriptionItemType.GOBBLE]: false,
    [SubscriptionItemType.MOOO]: false,
    [SubscriptionItemType.OINK]: false,
    [SubscriptionItemType.QUACK]: false,
    [SubscriptionItemType.SPLASH]: false,
    [SubscriptionItemType.BAAA]: false,
    [SubscriptionItemType.SWISH]: false,
    [SubscriptionItemType.SCOOP_PLANET]: false,
    [SubscriptionItemType.SCOOP_HEALTH]: false,
    [SubscriptionItemType.NIBBLES_SALMON]: false,
    [SubscriptionItemType.NIBBLES_CHICKEN]: false,
    [SubscriptionItemType.NIBBLES_SALMON_POUCH]: true,
    [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: true,
    [SubscriptionItemType.NIBBLES_TUNA_POUCH]: true,
    [SubscriptionItemType.SPRINKLES]: false,
    [SubscriptionItemType.TOTE_BAG]: false,
    [SubscriptionItemType.SHREDDED_CLUCK]: false,
    [SubscriptionItemType.SHREDDED_GOBBLE]: false,
  };

export function isNibblesPouchProduct(product: string): product is TNibblesPouchProduct {
  return !!(IS_NIBBLES_POUCH_PRODUCT as Partial<Record<string, boolean>>)[product];
}

export const NIBBLES_POUCH_PRODUCTS = Object.values(SubscriptionItemType).filter(isNibblesPouchProduct);

export const NIBBLES_POUCH_MULTIBUY_THRESHOLD = 3;
export const NIBBLES_POUCH_MULTIBUY_DISCOUNT_PERCENTAGE = 0.1;
